import React, { memo } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { Modal } from 'Components/Modal';
import { Label } from 'Components/Label';
import { PurpleButton } from 'Components/Button';

import { ValidateBackGround } from 'Components/Validation';
import DatePicker from 'react-datepicker';

import formClasses from 'Themes/form/form.module.css';
import classes from './editTimeCardModal.module.css';

import 'react-datepicker/dist/react-datepicker-min.module.css';

interface TimecardType {
  id: number;
  name: string;
}

interface Props {
  // modal variables
  isOpen: boolean;
  canEdit: boolean;
  formErrors: any;
  // employee variables
  employees: any[];
  selectedEmployeeId: number;
  setSelectedEmployee: (e: any) => void;
  // project variables
  projects: any[];
  selectedProjectId: number;
  setSelectedProject: (e: any) => void;
  // type variables
  timecardTypes: TimecardType[];
  selectedTypeId: number;
  onTypeChange: (e: any) => void;
  // date variables
  startDate: any;
  endDate: any;
  // modal functions
  modalCloseClick: (e: any) => void;
  onEditButtonClick: (e: any) => void;
  // date functions
  onChangeStartDate: (e: any) => void;
  onChangeEndDate: (e: any) => void;
}

const EditTimeCardModal = ({
  isOpen,
  canEdit,
  employees,
  selectedEmployeeId,
  setSelectedEmployee,
  projects,
  selectedProjectId,
  setSelectedProject,
  formErrors,
  timecardTypes,
  selectedTypeId,
  onTypeChange,
  startDate,
  endDate,
  modalCloseClick,
  onEditButtonClick,
  onChangeStartDate,
  onChangeEndDate,
}: Props) => (
  <Modal
    id="edit-timecard-modal"
    classes={classes}
    title="Edit Timecard"
    isOpen={isOpen}
    modalHeader
    dataBsBackdrop="static"
    dataBsKeyboard="false"
    modalCloseClick={modalCloseClick}
  >
    <form className={`requires-validation g-3 ${formClasses.formBase}`} noValidate action="#">
      <div className={classes.fieldGroup}>
        <ValidateBackGround isValid={!formErrors?.user_id?.length}>
          <Label ariaLabel="Employee" className={formClasses.label} htmlFor="employee">
            Employee
          </Label>
          <select
            className={classes.typeSelect}
            value={selectedEmployeeId || ''}
            onChange={(e) => setSelectedEmployee(Number(e.target.value))}
          >
            <option value="" disabled>Select Employee</option>
            {employees?.map((emp: any) => (
              <option key={emp.id} value={emp.id}>
                {emp.name}
              </option>
            ))}
          </select>
          <div
            className={`${formClasses.invalidFieldFeedback} invalid-feedback ${
              formErrors?.user_id?.length ? 'd-block' : ''
            }`}
          >
            {formErrors?.user_id?.[0]}
          </div>
        </ValidateBackGround>
      </div>
      <div className={classes.fieldGroup}>
        <ValidateBackGround isValid={!formErrors?.project_id?.length}>
          <Label ariaLabel="Project" className={formClasses.label} htmlFor="project">
            Project
          </Label>
          <select
            className={classes.typeSelect}
            value={selectedProjectId || ''}
            onChange={(e) => setSelectedProject(Number(e.target.value))}
          >
            <option value="" disabled>Select Project</option>
            {projects?.map((proj: any) => (
              <option key={proj.id} value={proj.id}>
                {proj.name}
              </option>
            ))}
          </select>
          <div
            className={`${formClasses.invalidFieldFeedback} invalid-feedback ${
              formErrors?.project_id?.length ? 'd-block' : ''
            }`}
          >
            {formErrors?.project_id?.[0]}
          </div>
        </ValidateBackGround>
      </div>
      <div className={classes.fieldGroup}>
        <Label ariaLabel="Type" className={formClasses.label} htmlFor="timecardType">
          Type
        </Label>
        <select
          className={classes.typeSelect}
          value={selectedTypeId || ''}
          onChange={(e) => onTypeChange(Number(e.target.value))}
        >
          <option value="" disabled>Select Type</option>
          {timecardTypes?.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
      </div>
      <div className={classes.fieldGroup}>
        <ValidateBackGround isValid={!formErrors?.time_in?.length}>
          <Label ariaLabel="Clock In Time" className={formClasses.label} htmlFor="clockInTime">
            Clock In
          </Label>
          <div className={classes.dateTimeRow}>
            <DatePicker
              selected={startDate}
              onChange={onChangeStartDate}
              maxDate={new Date()}
              dateFormat="MM/dd/yyyy"
              placeholderText="Select date"
              className={classes.dateField}
              calendarClassName={classes.calendar}
            />
            <DatePicker
              selected={startDate}
              onChange={onChangeStartDate}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="h:mm aa"
              placeholderText="Select time"
              className={classes.timeField}
            />
          </div>
          <div
            className={`${formClasses.invalidFieldFeedback} invalid-feedback ${
              formErrors?.time_in?.length ? 'd-block' : ''
            }`}
          >
            {formErrors?.time_in?.[0]}
          </div>
        </ValidateBackGround>
      </div>
      <div className={classes.fieldGroup}>
        <ValidateBackGround isValid={!formErrors?.time_out?.length}>
          <Label ariaLabel="Clock Out Time" className={formClasses.label} htmlFor="clockOutTime">
            Clock Out
          </Label>
          <div className={classes.dateTimeRow}>
            <DatePicker
              selected={endDate}
              onChange={onChangeEndDate}
              maxDate={new Date()}
              dateFormat="MM/dd/yyyy"
              placeholderText="Select date"
              className={classes.dateField}
              calendarClassName={classes.calendar}
            />
            <DatePicker
              selected={endDate}
              onChange={onChangeEndDate}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="h:mm aa"
              placeholderText="Select time"
              className={classes.timeField}
            />
          </div>
          <div
            className={`${formClasses.invalidFieldFeedback} invalid-feedback ${
              formErrors?.time_out?.length ? 'd-block' : ''
            }`}
          >
            {formErrors?.time_out?.[0]}
          </div>
        </ValidateBackGround>
      </div>
      <div className={`modal-footer ${classes.modalFooter}`}>
        <PurpleButton onClick={modalCloseClick} className={classes.cancelButton}>
          Cancel
        </PurpleButton>
        <PurpleButton onClick={onEditButtonClick} disabled={!canEdit} className={classes.editButton}>
          Save
        </PurpleButton>
      </div>
    </form>
  </Modal>
);

const EditTimeCardModalMemo = memo(EditTimeCardModal, areEqual);

export { EditTimeCardModalMemo as EditTimeCardModal };
